import * as Tabs from '@radix-ui/react-tabs'
import { Button, message, Spin, Tooltip } from 'antd'
import { FeatureFormMode } from 'components/form/formTypes'
import { EditableFeatureForm } from 'features/assetDatabase'
import {
  AssetModelComponentClassMap,
  getJsonFileParser,
} from 'features/assetModeling'
import { useRolodexEdit } from 'features/fde'
import { useGeckoLocale } from 'features/locale'
import { useGeckoPermissions } from 'features/permissions'
import { AssetEditorState } from 'features/rightDrawer/drawerStates/assetEditorState'
import { FeaturesTab } from 'features/rightDrawer/drawerStates/crudWorkflows/editRichAssetModel/features/FeaturesTab'
import { ComponentTemplatesTab } from 'features/rightDrawer/drawerStates/crudWorkflows/editRichAssetModel/templates/ComponentTemplatesTab'
import { useComponentTemplates } from 'features/rightDrawer/drawerStates/crudWorkflows/editRichAssetModel/templates/useComponentTemplates'
import { useRolodexCache } from 'features/rolodex'
import React, { ChangeEvent, useState } from 'react'
import styled from 'styled-components'
import {
  GeckoAssetModel,
  GeckoComponent,
  GeckoComponentModelJSON,
  GeckoComponentModelType,
  GeckoEditableComponent,
  GeckoFeatureModelType,
  RolodexEntryPutJson,
} from 'types'
// eslint-disable-next-line boundaries/entry-point
import { ShipDeckAvailableFeatures } from 'types/assetModel/components/ship/geckoShipDeck'

const StyledHeaderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid ${(props) => props.theme.forecast.border};
`

const StyledComponentName = styled.div`
  font-size: 0.9375rem;
  line-height: 1.3125rem;
  color: ${(props) => props.theme.forecast.activeText};
  font-weight: 500;
`

const StyledAssetModelId = styled.div`
  font-size: 0.8125rem;
  line-height: 1.0625rem;
  color: ${(props) => props.theme.forecast.secondaryText};
  font-weight: 400;
`

const StyledTabsList = styled(Tabs.List)`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin-bottom: 0.75rem;
  padding: 0.25rem;
  background-color: ${(props) => props.theme.forecast.cardGroupBackground};
  border-radius: 0.375rem;
`

const StyledTabsTrigger = styled(Tabs.Trigger)<{ $isShipDeck?: boolean }>`
  background-color: ${(props) =>
    props.disabled ? 'transparent' : props.theme.forecast.defaultGhostButton};
  border: 1px solid ${(props) => props.theme.forecast.border};
  border-radius: 0.25rem;
  box-sizing: border-box;
  color: ${(props) =>
    props.disabled
      ? props.theme.forecast.secondaryText
      : props.theme.forecast.secondaryText};
  flex: 0 0 auto;
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
  font-weight: 500;
  transition: all 0.15s ease;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};

  &[data-state='active'] {
    background-color: ${(props) => props.theme.forecast.defaultButton};
    border-color: ${(props) => props.theme.forecast.primary};
    color: ${(props) => props.theme.forecast.defaultText};
    font-weight: 600;
  }

  &[data-state='inactive']:hover:not(:disabled) {
    background-color: ${(props) => props.theme.forecast.hoverGhostButton};
    border-color: ${(props) => props.theme.forecast.secondaryText};
    color: ${(props) => props.theme.forecast.activeText};
  }
`

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
`

const StyledTabsContent = styled(Tabs.Content)`
  padding: 0.5rem 0;
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
`

const StyledReturnButton = styled(Button)`
  background-color: transparent;
  border: 1px solid grey;
`

enum TabName {
  Info = 'info',
  Features = 'features',
  Compartments = 'compartments',
  NominalPlates = 'nominal-plates',
  Structures = 'structures',
  Templates = 'templates',
}

interface FeatureSelectionPageProps {
  assetModel: GeckoAssetModel
  component: GeckoComponent
  selectedComponentType: GeckoComponentModelType
  refreshData: () => Promise<void>
  goToSelectComponent?: () => void
  changeDrawerState?: (changeDrawerState: AssetEditorState) => void
  jsonSelector?: React.ReactNode
}

export const FeatureSelectionPage = ({
  assetModel,
  component,
  selectedComponentType,
  refreshData,
  goToSelectComponent,
  jsonSelector,
}: FeatureSelectionPageProps) => {
  const [activeTab, setActiveTab] = useState<TabName>(TabName.Info)
  const [jsonFile, setJsonFile] = useState(undefined)

  const { measurementSystem } = useGeckoLocale()
  const { permissions } = useGeckoPermissions()
  const { rolodexPut } = useRolodexEdit()
  const [messageApi, contextHolder] = message.useMessage()
  const { clearAll } = useRolodexCache()

  const handleFileUpload = React.useCallback(
    (uploadEvent: ChangeEvent<HTMLInputElement>) => {
      // If event target is null, return early
      if (uploadEvent.target === null) {
        return
      }

      // If the event target has no files, then do nothing
      const uploadEventTarget = uploadEvent.target as HTMLInputElement
      if (uploadEventTarget.files === null) {
        return
      }

      const fileReader = new FileReader()
      // If no file was uploaded, reset the fastfield file and return early
      if (uploadEventTarget.files.length === 0) {
        setJsonFile(undefined)
        return
      }

      // Otherwise, try to parse the file
      fileReader.readAsText(uploadEventTarget.files[0], 'UTF-8')
      fileReader.onload = (fileEvent) => {
        // If the fileEvent is null, then we have nothing to parse
        if (fileEvent.target === null) {
          return
        }
        try {
          const parsedFile = JSON.parse(fileEvent.target.result as string)
          setJsonFile(parsedFile)
        } catch {
          messageApi.error("Unable to parse this file! Are you sure it's JSON?")
        }
      }
    },
    [messageApi]
  )

  const handleSaveToRolodex = React.useCallback(
    (entries: RolodexEntryPutJson[]) => {
      rolodexPut(entries)
        .then(() => {
          return messageApi.success({
            content: 'Successfully saved Asset Model to Rolodex!',
            className: 'success-message',
          })
        })
        .catch(() => {
          return messageApi.error(
            'Failed to update the Asset Model in Rolodex! Please try again.'
          )
        })
        .finally(() => {
          clearAll()
          refreshData()
        })
    },
    [rolodexPut, messageApi, clearAll, refreshData]
  )

  const onSaveAssetModel = (editedAssetModel: RolodexEntryPutJson) => {
    // Keep the Component and RAM Component Model name the same
    const assetModelComponents = editedAssetModel.data.components as Record<
      string,
      unknown
    >
    const ramComponent = assetModelComponents[component.id] as Record<
      string,
      unknown
    >
    if (!ramComponent) {
      throw new Error(
        'Unable to update component. Could not extract name from asset model!'
      )
    }

    if (jsonParserFunction && jsonFile) {
      ramComponent.features = jsonParserFunction(jsonFile)
    }

    const editedComponent = component.raw
    editedComponent.data.name = ramComponent.name as string
    handleSaveToRolodex([editedAssetModel, editedComponent])
  }

  const assetModelComponentsJSON = assetModel.raw.data.components as Record<
    string,
    GeckoComponentModelJSON
  >
  const selectedComponentJSON = assetModelComponentsJSON[component.id]

  const assetModelComponent = (assetModel.data.components.get(component.id) ??
    AssetModelComponentClassMap.get(
      selectedComponentType
    )) as GeckoEditableComponent
  const formMode = assetModel.data.components.get(component.id)
    ? FeatureFormMode.Edit
    : FeatureFormMode.Add

  if (!assetModelComponent) {
    throw new Error(
      'Unable to edit Asset Model Component, the class is undefined!'
    )
  }

  const componentJSON =
    formMode === FeatureFormMode.Edit
      ? (assetModelComponent.asJSON() as Record<string, unknown>)
      : undefined
  const componentID =
    formMode === FeatureFormMode.Edit
      ? (assetModelComponent.id as string)
      : undefined

  const jsonParserFunction = getJsonFileParser(selectedComponentType)

  const { isLoading: isLoadingTemplates, applicableTemplates } =
    useComponentTemplates(selectedComponentType)

  const isShipDeck = selectedComponentType === GeckoComponentModelType.ShipDeck

  return (
    <div>
      {contextHolder}
      <StyledContainer>
        {goToSelectComponent ? (
          <StyledReturnButton onClick={goToSelectComponent}>
            Return to Component Selection
          </StyledReturnButton>
        ) : null}
        <StyledHeaderInfo>
          <StyledComponentName>{assetModelComponent.name}</StyledComponentName>
          <StyledAssetModelId>{assetModel.id}</StyledAssetModelId>
        </StyledHeaderInfo>
        <Tabs.Root defaultValue={activeTab}>
          <StyledTabsList>
            <StyledTabsTrigger
              value={TabName.Info}
              $isShipDeck={isShipDeck}
              onClick={() => setActiveTab(TabName.Info)}>
              Info
            </StyledTabsTrigger>
            {isShipDeck ? (
              <>
                <Tooltip
                  title={
                    !selectedComponentJSON
                      ? 'You must save the component info before adding features'
                      : undefined
                  }
                  placement={'bottomLeft'}>
                  <StyledTabsTrigger
                    value={TabName.Compartments}
                    $isShipDeck={isShipDeck}
                    disabled={!selectedComponentJSON}
                    onClick={() => setActiveTab(TabName.Compartments)}>
                    Compartments
                  </StyledTabsTrigger>
                </Tooltip>
                <Tooltip
                  title={
                    !selectedComponentJSON
                      ? 'You must save the component info before adding features'
                      : undefined
                  }
                  placement={'bottomLeft'}>
                  <StyledTabsTrigger
                    value={TabName.NominalPlates}
                    $isShipDeck={isShipDeck}
                    disabled={!selectedComponentJSON}
                    onClick={() => setActiveTab(TabName.NominalPlates)}>
                    Nominal Plates
                  </StyledTabsTrigger>
                </Tooltip>
                <Tooltip
                  title={
                    !selectedComponentJSON
                      ? 'You must save the component info before adding features'
                      : undefined
                  }
                  placement={'bottomLeft'}>
                  <StyledTabsTrigger
                    value={TabName.Structures}
                    $isShipDeck={isShipDeck}
                    disabled={!selectedComponentJSON}
                    onClick={() => setActiveTab(TabName.Structures)}>
                    Structures
                  </StyledTabsTrigger>
                </Tooltip>
              </>
            ) : (
              <Tooltip
                title={
                  !selectedComponentJSON
                    ? 'You must save the component info before adding features'
                    : undefined
                }
                placement={'bottomLeft'}>
                <StyledTabsTrigger
                  value={TabName.Features}
                  $isShipDeck={isShipDeck}
                  disabled={!selectedComponentJSON}
                  onClick={() => setActiveTab(TabName.Features)}>
                  Features
                </StyledTabsTrigger>
              </Tooltip>
            )}
            <StyledTabsTrigger
              value={TabName.Templates}
              $isShipDeck={isShipDeck}
              disabled={isLoadingTemplates || applicableTemplates.length === 0}
              onClick={() => setActiveTab(TabName.Templates)}>
              Templates
              {isLoadingTemplates ? (
                <Spin />
              ) : (
                ` (${applicableTemplates.length})`
              )}
            </StyledTabsTrigger>
          </StyledTabsList>
          <StyledTabsContent value={TabName.Info}>
            <EditableFeatureForm
              measurementSystem={measurementSystem}
              featureType={selectedComponentType}
              featureJSON={componentJSON}
              featureID={componentID}
              formMode={formMode}
              assetModel={assetModel}
              selectedComponent={component}
              onSave={onSaveAssetModel}
              userPermissions={permissions}
              jsonSelector={jsonSelector}
            />
            {!!jsonParserFunction && !componentJSON && (
              <div style={{ paddingTop: '25px' }}>
                <p>
                  Optionally, when creating an asset model, you may upload a
                  JSON file to automatically enrich the asset model. Refer to
                  the documentation for the correct schema. After uploading the
                  file below, select &quot;Save&quot; button above.
                </p>
                <input type={'file'} onChange={handleFileUpload} />
              </div>
            )}
          </StyledTabsContent>
          {isShipDeck ? (
            <>
              <StyledTabsContent value={TabName.Compartments}>
                <FeaturesTab
                  selectedComponentType={selectedComponentType}
                  selectedComponentJSON={selectedComponentJSON}
                  assetModel={assetModel}
                  component={component}
                  handleSaveToRolodex={handleSaveToRolodex}
                  jsonSelector={jsonSelector}
                  assetModelFeatureTypeFilter={
                    GeckoFeatureModelType.ShipDeckCompartment
                  }
                />
              </StyledTabsContent>
              <StyledTabsContent value={TabName.NominalPlates}>
                <FeaturesTab
                  selectedComponentType={selectedComponentType}
                  selectedComponentJSON={selectedComponentJSON}
                  assetModel={assetModel}
                  component={component}
                  handleSaveToRolodex={handleSaveToRolodex}
                  jsonSelector={jsonSelector}
                  assetModelFeatureTypeFilter={
                    GeckoFeatureModelType.PolygonalPlate
                  }
                />
              </StyledTabsContent>
              <StyledTabsContent value={TabName.Structures}>
                <FeaturesTab
                  selectedComponentType={selectedComponentType}
                  selectedComponentJSON={selectedComponentJSON}
                  assetModel={assetModel}
                  component={component}
                  handleSaveToRolodex={handleSaveToRolodex}
                  jsonSelector={jsonSelector}
                  assetModelFeatureTypeFilter={ShipDeckAvailableFeatures.filter(
                    (featureType) =>
                      featureType !==
                        GeckoFeatureModelType.ShipDeckCompartment &&
                      featureType !== GeckoFeatureModelType.PolygonalPlate
                  )}
                />
              </StyledTabsContent>
            </>
          ) : (
            <StyledTabsContent value={TabName.Features}>
              <FeaturesTab
                selectedComponentType={selectedComponentType}
                selectedComponentJSON={selectedComponentJSON}
                assetModel={assetModel}
                component={component}
                handleSaveToRolodex={handleSaveToRolodex}
                jsonSelector={jsonSelector}
              />
            </StyledTabsContent>
          )}
          <StyledTabsContent value={TabName.Templates}>
            <ComponentTemplatesTab
              selectedComponent={assetModel.data.components.get(component.id)}
              applicableTemplates={applicableTemplates}
              isLoadingTemplates={isLoadingTemplates}
              assetModel={assetModel}
              forceRefresh={refreshData}
            />
          </StyledTabsContent>
        </Tabs.Root>
      </StyledContainer>
    </div>
  )
}
